import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import '../styles/DoctorAvailabilityModal.css';
import { RRule } from 'rrule';

// Function to validate that the end time is later than the start time
const validateTimeRange = (start, end) => {
  const [startHours, startMinutes] = start.split(':').map(Number);
  const [endHours, endMinutes] = end.split(':').map(Number);

  const startTime = new Date(0, 0, 0, startHours, startMinutes);
  const endTime = new Date(0, 0, 0, endHours, endMinutes);

  return endTime > startTime; // Returns true if endTime is later than startTime
};

const DoctorAvailabilityModal = ({ slot, saveSlot, closeModal, availableSlots, deleteSlot, isEdit, clinicId, selectedPractitioner }) => {
  const { t } = useTranslation();

  const [availabilityType, setAvailabilityType] = useState('available');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [recurring, setRecurring] = useState(false); // New state for recurrence
  const [recurrenceType, setRecurrenceType] = useState('none'); // None, daily, weekly, etc.
  const [recurrenceEnd, setRecurrenceEnd] = useState(''); // End date for recurrence
  const [validationError, setValidationError] = useState(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false); // Checkbox state for delete confirmation
  const [formattedDate, setFormattedDate] = useState('');

  // if (selectedPractitioner) {
  //   console.log('Validating for Practitioner:', selectedPractitioner.name);
  // }

  
  // Pre-populate fields if editing
  useEffect(() => {
    if (isEdit && slot) {
      setStartTime(new Date(slot.start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
      setEndTime(new Date(slot.end).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
      setAvailabilityType(slot.title === 'Available' ? 'available' : 'unavailable');
    }
  }, [isEdit, slot]);

  useEffect(() => {
    if (slot) {
      const selectedDate = new Date(slot.start).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      setFormattedDate(selectedDate);
    }
  }, [slot]);
  
  
  // New function to check for slot conflicts(for single or recurring slots)
  const checkForConflicts = (start, end, recurrenceType, recurrenceEnd, slot) => {
    // If no recurrence, only check the single slot
    if (!recurrenceType || recurrenceType === 'none') {
      const newStart = new Date(`${slot.start.toDateString()} ${start}`).getTime();
      const newEnd = new Date(`${slot.start.toDateString()} ${end}`).getTime();
      return availableSlots.some(existingSlot => {

        if(existingSlot.status === 'CA') return false;
        // Check conflicts only within the same practitioner
        if (
          selectedPractitioner?.id === existingSlot?.practitioner?.id &&
          (!isEdit || existingSlot.id !== slot.id) // Ignore the slot being edited
        ) {
          const existingStart = new Date(existingSlot.start).getTime();
          const existingEnd = new Date(existingSlot.end).getTime();
          return (newStart < existingEnd && newEnd > existingStart); // Check overlap
        }
        return false; // No conflict if the practitioner doesn't match
      });
    }
  
    // If recurrence, check each occurrence
    const rule = new RRule({
      freq: recurrenceType === 'daily' ? RRule.DAILY :
            recurrenceType === 'weekly' ? RRule.WEEKLY :
            recurrenceType === 'monthly' ? RRule.MONTHLY : null,
      dtstart: new Date(`${slot.start.toDateString()} ${start}`),
      until: recurrenceEnd ? new Date(recurrenceEnd) : null,
    });
  
    const occurrences = rule.all();
  
    return occurrences.some(occurrence => {
      const newStart = new Date(`${occurrence.toDateString()} ${start}`).getTime();
      const newEnd = new Date(`${occurrence.toDateString()} ${end}`).getTime();
      return availableSlots.some(existingSlot => {
        if (existingSlot.status === 'CA') return false;
        // Check conflicts only within the same practitioner
        if (
          selectedPractitioner?.id === existingSlot?.practitioner?.id &&
          (!isEdit || existingSlot.id !== slot.id) // Ignore the slot being edited
        ) {
          const existingStart = new Date(existingSlot.start).getTime();
          const existingEnd = new Date(existingSlot.end).getTime();
          return (newStart < existingEnd && newEnd > existingStart); // Check overlap for each occurrence
        }
        return false; // No conflict if the practitioner doesn't match
      });
    });
  };
  
  

  // Generate recurring or single slots
  const generateRecurringSlots = ({ startTime, endTime, recurrenceType, recurrenceEnd, availabilityType, slot }) => {
    const newSlots = [];

    if (!isEdit && recurring && recurrenceType !== 'none') {  // Only allow recurrence when not in edit mode
      const rule = new RRule({
        freq: recurrenceType === 'daily' ? RRule.DAILY :
              recurrenceType === 'weekly' ? RRule.WEEKLY :
              recurrenceType === 'monthly' ? RRule.MONTHLY : null,  // Add Monthly
        dtstart: new Date(`${slot.start.toDateString()} ${startTime}`),
        until: recurrenceEnd ? new Date(recurrenceEnd) : null,
      });

      const occurrences = rule.all();

      occurrences.forEach(occurrence => {
        const start = new Date(occurrence);
        const end = new Date(`${occurrence.toDateString()} ${endTime}`); // Correct end time
        newSlots.push({
          id: `${start.getTime()}-${end.getTime()}`, // Unique ID
          start,
          end,
          title: availabilityType === 'available' ? 'Available' : 'Unavailable',
          status: availabilityType === 'available' ? 'AV' : 'UA',
        });
      });
    } else {
      // Single slot logic or edit mode
      const start = new Date(`${slot.start.toDateString()} ${startTime}`);
      const end = new Date(`${slot.start.toDateString()} ${endTime}`); // Correct end time
      newSlots.push({
        id: `${start.getTime()}-${end.getTime()}`, // Unique ID
        start,
        end,
        title: availabilityType === 'available' ? 'Available' : 'Unavailable',
        status: availabilityType === 'available' ? 'AV' : 'UA',
      });
    }

    return newSlots;
  };


  // Handle saving slots (single or recurring)
  const handleSubmitAvailability = () => {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
    // Ensure no past slots are being saved
    if (new Date(slot.start) < todayStart) {
      setValidationError("Cannot mark availability for past dates.");
      return; // Prevent submission
    }
  
    if (!validateTimeRange(startTime, endTime)) {
      setValidationError("End time must be later than start time.");
      return;
    }
  
    // Check for slot conflicts before proceeding
    if (checkForConflicts(startTime, endTime, recurrenceType, recurrenceEnd, slot)) {
      setValidationError(
        `The selected time slot conflicts with an existing slot for ${selectedPractitioner?.name}. Please choose a different time.`
      );
      return; // Prevent submission if conflict is found
    }
    
    if (recurring && recurrenceType !== 'none' && !recurrenceEnd) {
      setValidationError("Please provide an end date for the recurrence.");
      return;
    }

    
    // Generate the new or recurring slots
    const newSlots = generateRecurringSlots({
      startTime,
      endTime,
      recurrenceType,
      recurrenceEnd,
      availabilityType,
      slot,
    });
  
    // If no validation errors, save the slot(s)
    saveSlot(newSlots);
    closeModal();
  };
  

  // Handle delete confirmation via checkbox
  const handleDeleteSlot = () => {
    deleteSlot(slot); 
    closeModal();
  };
  

  return (
    <div className="availability-specific-modal-backdrop">
      <div className="availability-specific-modal-content">
      <h3 className="availability-specific-modal-header">
      {isEdit 
          ? (slot?.status === "UA" 
              ? t("availability_management.remove_unavailability") 
              : t("availability_management.cancel_availability")) 
          : t("availability_management.set_availability")}
      </h3>
        {formattedDate && (
          <p className="availability-specific-selected-date">
          {isEdit
              ? (slot?.status === "UA"
                  ? t("availability_management.removing_unavailability")
                  : t("availability_management.cancelling_availability"))
              : t("availability_management.setting_availability")}
          <span className="highlighted-date">{formattedDate}</span>
        </p>        
        )}
        {/* Availability Type (Available or Unavailable) */}
        <div className="availability-specific-availability-group">
          <label className="availability-specific-radio-label">
            <input
              type="radio"
              value="available"
              checked={availabilityType === 'available'}
              onChange={() => setAvailabilityType('available')}
              className="availability-specific-radio-input"
              disabled={isEdit}
            />
            {t("availability_management.available")}
          </label>
          <label className="availability-specific-radio-label">
            <input
              type="radio"
              value="unavailable"
              checked={availabilityType === 'unavailable'}
              onChange={() => setAvailabilityType('unavailable')}
              className="availability-specific-radio-input"
              style={{ marginLeft: '1rem' }}
              disabled={isEdit}
            />
            {t("availability_management.unavailable")}
          </label>
        </div>
  
        {/* Custom Time Fields */}
        <div className="availability-specific-custom-time-selection">
          <div className="availability-specific-time-label-group">
            <label className="availability-specific-label">
            {t("availability_management.start_time")}:
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="availability-specific-time-input"
                disabled={isEdit}
              />
            </label>
            <label className="availability-specific-label">
            {t("availability_management.end_time")}:
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="availability-specific-time-input"
                disabled={isEdit}
              />
            </label>
          </div>
        </div>

  
        {/* Recurrence Options */}
        {!isEdit && ( // Hide recurrence options in edit mode
          <div className="availability-specific-recurring-options">
            <label className="availability-specific-checkbox-label">
              <input
                type="checkbox"
                checked={recurring}
                onChange={() => setRecurring(!recurring)}
                className="availability-specific-checkbox-input"
              />
              {t("availability_management.recurring")}
            </label>

            {recurring && (
              <>
                <div className="availability-specific-recurring-row">
                <label className="availability-specific-label">
                  <span>
                    {t("availability_management.recurrence_type")}:
                    <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                  </span>
                  <select
                    value={recurrenceType}
                    onChange={(e) => setRecurrenceType(e.target.value)}
                    className="availability-specific-select"
                  >
                    <option value="none">{t("availability_management.none")}</option>
                    <option value="daily">{t("availability_management.daily")}</option>
                    <option value="weekly">{t("availability_management.weekly")}</option>
                    <option value="monthly">{t("availability_management.monthly")}</option>
                  </select>
                </label>


                  <label className="availability-specific-label">
                  <span>
                  {t("availability_management.end_recurrence")}:
                  <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                  </span>
                    <input
                      type="date"
                      value={recurrenceEnd}
                      onChange={(e) => setRecurrenceEnd(e.target.value)}
                      className="availability-specific-date-input"
                    />
                  </label>
                </div>
              </>
            )}
          </div>
        )}

        {validationError && <p style={{ color: 'red' }}>{validationError}</p>}
  
        {/* Delete Confirmation Message */}
        {isEdit && (
            <div className="delete-confirmation-section">
              <p className="delete-confirmation-text">
              {slot?.status === "UA"
                ? t("availability_management.confirm_remove_unavailability")
                : t("availability_management.confirm_cancel_slot")}
              </p>
              <label className="delete-confirmation-label">
                <input
                  type="checkbox"
                  checked={deleteConfirmed}
                  onChange={() => setDeleteConfirmed(!deleteConfirmed)}
                />
                <span>
                  {slot?.status === "UA"
                    ? t("availability_management.confirm_remove_unavailability_button")
                    : t("availability_management.confirm_cancel_slot_button")}
                </span>
              </label>
            </div>
          )}

        {/* Save and Cancel Buttons */}
          <div className="availability-specific-modal-buttons">
            {!isEdit ? ( 
              <button
              onClick={handleSubmitAvailability}
              className={`availability-specific-modal-save-button ${
                recurring && !recurrenceEnd ? 'disabled' : ''
              }`}
              disabled={recurring && !recurrenceEnd}
            >
              {t("availability_management.save_availability")}
            </button>
            
            
            ) : (

              <>
                  {slot?.status === "UA" ? (
                      // Remove button for Unavailable (UA) slots
                      <button
                          onClick={handleDeleteSlot}
                          className={`availability-specific-modal-delete-button ${!deleteConfirmed ? 'disabled' : ''}`}
                          disabled={!deleteConfirmed} 
                      >
                           {t("availability_management.remove_unavailability_button")}
                      </button>
                  ) : (
                      // Cancel Button for AV slots
                      <button
                          onClick={handleDeleteSlot}
                          className={`availability-specific-modal-delete-button ${!deleteConfirmed ? 'disabled' : ''}`}
                          disabled={!deleteConfirmed} 
                      >
                          {t("availability_management.cancel_availability_button")}
                      </button>
                  )}
              </>
            )}
            <button onClick={closeModal} className="availability-specific-modal-cancel-button">
            {t("availability_management.close_me")}
            </button>
          </div>

      </div>
    </div>
  );
  
};

export default DoctorAvailabilityModal;
